import { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Container,
  Typography,
  CircularProgress,
  IconButton,
  FormLabel,
} from "@mui/material";
import { createBlobInContainer } from "src/utils/azureBlob";
import { nhlPackTypes } from "src/utils/cardUtils";
import { get, put } from "src/utils/networkUtils";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateStoreItem } from "src/store/slices/storeItemsSlice";
import Iconify from "src/components/iconify/Iconify";

const defaultItem = {
  name: "",
  description: "",
  image: "",
  listImage: "",
  productId: "",
  amount: "",
  store: 0,
};

export default function StoreItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeItemsByType = useSelector((state) => state.storeItems.items);

  const [packTypes, setPackTypes] = useState([]);
  const [versusPackTypes, setVersusPackTypes] = useState([]);
  const [item, setItem] = useState(defaultItem);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let found = null;
    Object.values(storeItemsByType).forEach((group) => {
      group.forEach((i) => {
        if (i.marketplaceStoreItemId === Number(id)) found = i;
      });
    });

    if (found) {
      setItem(found);
      setLoading(false);
    } else {
      get(`/admin/marketplace/store-items/${id}`)
        .then((res) => {
          setItem(res);
          dispatch(updateStoreItem(res));
        })
        .finally(() => setLoading(false));
    }
  }, [id, storeItemsByType, dispatch]);

  useEffect(() => {
    get(`/admin/packs/types`).then((response) => {
      setVersusPackTypes(response.versusPackTypes);
      setPackTypes(
        response.packTypes.filter((pack) => nhlPackTypes.includes(pack.value))
      );
    });
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setItem((prev) => ({ ...prev, [id]: value }));
  };

  const onSave = () => {
    const payload = { ...item, tenantId: 1 };
    put(`/admin/marketplace/store-items/${id}`, payload)
      .then(() => {
        dispatch(updateStoreItem(payload));
        navigate(-1);
      })
      .catch((error) => console.error("Failed to save store item:", error));
  };

  const uploadFiles = (event, name) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      createBlobInContainer(file).then(() => {
        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        setItem((prev) => ({
          ...prev,
          [name]: `https://${storageAccountName}.blob.core.windows.net/${containerName}/${file.name}`,
        }));
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Store
      </Typography>
      <Grid container>
        <Grid item xs={12} sx={{ m: 0, p: 1 }}>
          <TextField
            id="productId"
            label="Product ID"
            fullWidth
            value={item.productId}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={4} sx={{ m: 0, p: 1 }}>
          <TextField
            id="name"
            label="Name"
            fullWidth
            value={item.name}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={4} sx={{ m: 0, p: 1 }}>
          <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              id="itemType"
              value={item.itemType || ""}
              onChange={(e) =>
                setItem((prev) => ({ ...prev, itemType: e.target.value }))
              }
            >
              <MenuItem value="0">Coins</MenuItem>
              <MenuItem value="1">Pack</MenuItem>
              <MenuItem value="2">AllAccessPass</MenuItem>
              <MenuItem value="3">Versus Pack</MenuItem>
              <MenuItem value="4">Ticket</MenuItem>
              <MenuItem value="5">MonthlyAllAccessPass</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {item.itemType === "1" && (
          <Grid item xs={4} sx={{ m: 0, p: 1 }}>
            <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
              <InputLabel id="packtype-label">Pack Type</InputLabel>
              <Select
                labelId="packtype-label"
                id="packType"
                value={item.packType || ""}
                onChange={(e) =>
                  setItem((prev) => ({ ...prev, packType: e.target.value }))
                }
              >
                {packTypes.map((packType) => (
                  <MenuItem value={packType.value} key={packType.value}>
                    {packType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {item.itemType === "3" && (
          <Grid item xs={4} sx={{ m: 0, p: 1 }}>
            <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
              <InputLabel id="versuspacktype-label">Pack Type</InputLabel>
              <Select
                labelId="versuspacktype-label"
                id="packType"
                value={item.packType || ""}
                onChange={(e) =>
                  setItem((prev) => ({ ...prev, packType: e.target.value }))
                }
              >
                {versusPackTypes.map((packType) => (
                  <MenuItem value={packType.value} key={packType.value}>
                    {packType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sx={{ m: 0, p: 1 }}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            value={item.description}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ m: 0, p: 1 }}>
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id="store-label">Store</InputLabel>
            <Select
              labelId="store-label"
              id="store"
              value={item.store || ""}
              onChange={(e) =>
                setItem((prev) => ({ ...prev, store: e.target.value }))
              }
            >
              <MenuItem value="0">Apple</MenuItem>
              <MenuItem value="1">Google</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3} sx={{ m: 0, p: 1 }}>
          <TextField
            id="amount"
            label="Coins Amount"
            fullWidth
            value={item.amount}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ m: 0, p: 1 }}>
          <TextField
            id="price"
            label="Price Label (Android)"
            fullWidth
            value={item.price}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3} sx={{ m: 0, p: 1 }}>
          <TextField
            id="priceValue"
            label="Price Value"
            fullWidth
            value={item.priceValue}
            variant="standard"
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={6}>
          <FormLabel>Image</FormLabel>
          <IconButton color="primary" component="label">
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={(e) => uploadFiles(e, "image")}
            />
            <Iconify icon={"eva:upload-outline"} sx={{ mr: 2 }} />
          </IconButton>
          {item.image && (
            <img
              style={{ margin: "0 auto", width: 100, background: "grey" }}
              alt="Store Item"
              src={item.image}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <FormLabel>List Image</FormLabel>
          <IconButton color="primary" component="label">
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={(e) => uploadFiles(e, "listImage")}
            />
            <Iconify icon={"eva:upload-outline"} sx={{ mr: 2 }} />
          </IconButton>
          {item.listImage && (
            <img
              style={{ margin: "0 auto", width: 100, background: "grey" }}
              alt="Store item"
              src={item.listImage}
            />
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!item.name || !item.description || !item.image}
        >
          Save
        </Button>
      </Box>
    </Container>
  );
}
